import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Music, Sparkles, Volume2, X } from "lucide-react";
import { GAMES } from "./games/games";
import { getCtx } from "./games/audio";

export default function Experience() {
  const [phase, setPhase] = useState("idle"); // idle | spinning | playing
  const [highlight, setHighlight] = useState(-1);
  const [chosen, setChosen] = useState(null);
  const timeoutsRef = useRef([]);

  const startSpin = () => {
    getCtx(); // unlock audio context on user gesture
    setPhase("spinning");
    setChosen(null);

    // Wheel of fortune logic: cycle through cards, slowing down progressively
    const finalIdx = Math.floor(Math.random() * GAMES.length);
    const totalSteps = 30 + finalIdx; // ensures we land on finalIdx after N full cycles
    let delay = 60; // ms — starts fast
    let cumulative = 0;

    timeoutsRef.current = [];
    for (let step = 0; step < totalSteps; step++) {
      cumulative += delay;
      const idx = step % GAMES.length;
      const t = setTimeout(() => setHighlight(idx), cumulative);
      timeoutsRef.current.push(t);
      // Ease-out: each step slightly slower
      delay = delay + Math.pow(step / totalSteps, 2) * 50;
    }
    // Final stop
    const stopTimeout = setTimeout(() => {
      setHighlight(finalIdx);
      setChosen(GAMES[finalIdx]);
      setPhase("playing");
    }, cumulative + 200);
    timeoutsRef.current.push(stopTimeout);
  };

  const closeGame = () => {
    timeoutsRef.current.forEach(clearTimeout);
    setPhase("idle");
    setHighlight(-1);
    setChosen(null);
  };

  return (
    <section
      id="experience"
      className="relative py-28 md:py-40 px-6 md:px-12 lg:px-20 bg-cream overflow-hidden"
      data-testid="experience-section"
    >
      <motion.div animate={{ x: [0, 40, 0], y: [0, -30, 0] }} transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }} className="absolute top-10 -left-20 w-[500px] h-[500px] rounded-full bg-terracotta/15 blur-[120px] pointer-events-none" />
      <motion.div animate={{ x: [0, -50, 0], y: [0, 30, 0] }} transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }} className="absolute bottom-10 -right-20 w-[500px] h-[500px] rounded-full bg-sage/20 blur-[120px] pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 1 }} className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-terracotta/10 border border-terracotta/20 mb-8">
            <Volume2 size={14} className="text-terracotta" />
            <span className="text-[10px] uppercase tracking-[0.25em] text-terracotta font-medium">Mettez le son</span>
          </div>
          <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light leading-[0.95] text-ink">
            Une petite <span className="italic-serif text-terracotta">pause sonore</span> ?
          </h2>
          <p className="mt-8 text-lg md:text-xl text-ink/75 font-light leading-relaxed">
            Le sort choisira pour vous l'un de nos 6 mini-jeux sonores. Une bulle ludique pour finir en douceur.
          </p>
        </motion.div>

        {/* Cards grid with highlight wheel */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-12">
          {GAMES.map((g, i) => {
            const isActive = highlight === i;
            const isChosen = chosen?.id === g.id && phase === "playing";
            return (
              <motion.div
                key={g.id}
                animate={{
                  scale: isActive ? 1.08 : 1,
                  y: isActive ? -8 : 0,
                }}
                transition={{ duration: 0.15 }}
                className={`relative aspect-square rounded-2xl border transition-all duration-300 flex flex-col items-center justify-center p-4 ${
                  isActive
                    ? "bg-cream border-terracotta shadow-[0_20px_40px_-10px_rgba(200,107,82,0.5)]"
                    : "bg-sand border-ink/8"
                }`}
                data-testid={`game-card-${g.id}`}
              >
                {isChosen && (
                  <motion.div
                    layoutId="chosen-ring"
                    className="absolute inset-0 rounded-2xl ring-2 ring-terracotta pointer-events-none"
                  />
                )}
                <div className="text-4xl md:text-5xl mb-2">{g.emoji}</div>
                <div className="font-serif text-sm md:text-base text-ink text-center leading-tight">{g.name}</div>
                <div className="text-[10px] text-ink/50 text-center mt-1 hidden md:block">{g.desc}</div>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center">
          <button
            onClick={startSpin}
            disabled={phase === "spinning"}
            className="group relative inline-flex items-center gap-3 px-10 py-5 rounded-full bg-terracotta text-cream text-sm uppercase tracking-[0.2em] hover:scale-105 transition-all duration-500 pulse-ring disabled:opacity-60 disabled:cursor-wait"
            data-testid="experience-cta"
          >
            <Music size={18} className={phase === "spinning" ? "animate-spin" : "group-hover:rotate-12 transition"} />
            {phase === "spinning" ? "Sélection en cours…" : phase === "playing" ? "Tirer un autre jeu" : "Démarrer l'expérience"}
            <Sparkles size={16} />
          </button>
          <p className="mt-6 text-xs uppercase tracking-[0.2em] text-ink/40">6 mini-jeux sonores · synthèse audio native · gratuit</p>
        </div>
      </div>

      {/* Modal — chosen game */}
      <AnimatePresence>
        {phase === "playing" && chosen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-walnut/70 backdrop-blur-md"
            onClick={closeGame}
            data-testid="game-modal"
          >
            <motion.div
              initial={{ scale: 0.85, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              transition={{ type: "spring", stiffness: 200, damping: 22 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-2xl bg-cream rounded-3xl p-8 md:p-12 shadow-2xl"
            >
              <button onClick={closeGame} className="absolute top-5 right-5 w-10 h-10 rounded-full bg-sand hover:bg-terracotta hover:text-cream flex items-center justify-center transition" data-testid="modal-close">
                <X size={18} />
              </button>
              <div className="text-center mb-6">
                <div className="text-5xl mb-3">{chosen.emoji}</div>
                <h3 className="font-serif text-3xl md:text-4xl text-ink">{chosen.name}</h3>
                <div className="w-12 h-[1px] bg-terracotta mx-auto mt-3" />
              </div>
              <div className="mt-6">
                <chosen.Component />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
